/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calculator, Settings, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { useTheme } from '../../contexts/ThemeContext';
import { customerPortalService } from '../../services/customerPortalApi';

const CustomerPriceCalculator = () => {
  const { isDark } = useTheme();
  const [fabricInfos, setFabricInfos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [pastalEfficiency, setPastalEfficiency] = useState(0.7);
  const [tempPastalEfficiency, setTempPastalEfficiency] = useState(0.7);
  
  // İlk kumaş
  const [fabric1, setFabric1] = useState({
    stockCardId: '',
    colorType: '',
    fabricInfoId: '',
    quantity: '',
    result: null
  });
  
  // Karşılaştırma kumaşları
  const [fabric2, setFabric2] = useState({
    stockCardId: '',
    colorType: '',
    fabricInfoId: '',
    result: null
  });
  const [fabric3, setFabric3] = useState({
    stockCardId: '',
    colorType: '',
    fabricInfoId: '',
    result: null
  });
  const [fabric4, setFabric4] = useState({
    stockCardId: '',
    colorType: '',
    fabricInfoId: '',
    result: null
  });
  
  const [comparisonCount, setComparisonCount] = useState(0);
  const hasAutoCalculatedRef = useRef({ fabric1: false, fabric2: false, fabric3: false, fabric4: false });
  const calculationRequestIdRef = useRef({ fabric1: 0, fabric2: 0, fabric3: 0, fabric4: 0 });

  const loadFabricInfos = async () => {
    try {
      const response = await customerPortalService.get('/fabric-info', { params: { activeOnly: 'true' } });
      if (response.data.success) {
        const data = response.data.data || [];
        // Renk önceliğine göre sırala
        const sortedFabrics = data.sort((a, b) => {
          const colorOrder = { 'SIYAH': 1, 'RENKLI': 2, 'BEYAZ': 3, 'DIJITAL': 4 };
          return (colorOrder[a.colorType] || 5) - (colorOrder[b.colorType] || 5);
        });
        setFabricInfos(sortedFabrics);
        
        if (data.length === 0) {
          toast.error('Henüz kumaş bilgisi eklenmemiş.');
        }
      }
    } catch (error) {
      console.error('Kumaş bilgileri yüklenemedi:', error);
      toast.error('Kumaş bilgileri yüklenemedi: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCalculate = useCallback(async (fabricNumber) => {
    const fabricMap = {
      1: { fabric: fabric1, setFabric: setFabric1 },
      2: { fabric: fabric2, setFabric: setFabric2 },
      3: { fabric: fabric3, setFabric: setFabric3 },
      4: { fabric: fabric4, setFabric: setFabric4 }
    };
    const selected = fabricMap[fabricNumber];
    if (!selected) return;
    const { fabric, setFabric } = selected;

    // Karşılaştırma için ana kumaşın metraj ortalamasını kullan
    const quantity = fabricNumber === 1 ? fabric.quantity : fabric1.quantity;
    const baseMetersMin = fabric1.result?.minMeters ? parseFloat(fabric1.result.minMeters) : null;
    const baseMetersMax = fabric1.result?.maxMeters ? parseFloat(fabric1.result.maxMeters) : null;
    const baseMetersAvg = (baseMetersMin && baseMetersMax) ? (baseMetersMin + baseMetersMax) / 2 : null;

    if (!fabric.fabricInfoId) {
      toast.error('Lütfen kumaş seçin');
      return;
    }

    if (fabricNumber === 1 && !quantity) {
      toast.error('Lütfen miktar girin');
      return;
    }

    if (fabricNumber !== 1 && (!baseMetersAvg || !Number.isFinite(baseMetersAvg))) {
      toast.error('Önce ana kumaş için hesaplama yapın');
      return;
    }

    setLoading(true);
    try {
      const fabricKey = `fabric${fabricNumber}`;
      calculationRequestIdRef.current[fabricKey] += 1;
      const requestId = calculationRequestIdRef.current[fabricKey];

      const payload = {
        fabricInfoId: fabric.fabricInfoId,
        pastalEfficiency: pastalEfficiency
      };

      if (fabricNumber === 1) {
        payload.quantity = parseFloat(quantity);
      } else {
        payload.baseMetersAvg = baseMetersAvg;
        payload.baseMetersMin = baseMetersMin;
        payload.baseMetersMax = baseMetersMax;
      }

      const response = await customerPortalService.post('/fabric-info/calculate', payload);

      if (response.data.success) {
        if (requestId !== calculationRequestIdRef.current[fabricKey]) {
          return;
        }
        setFabric((prev) => ({
          ...prev,
          result: response.data.data
        }));
      }
    } catch (error) {
      console.error('Hesaplama hatası:', error);
      toast.error(error.response?.data?.message || 'Hesaplama başarısız');
    } finally {
      setLoading(false);
    }
  }, [fabric1, fabric2, fabric3, fabric4, pastalEfficiency]);

  useEffect(() => {
    loadFabricInfos();
  }, []);

  useEffect(() => {
    if (!fabric1.fabricInfoId || !fabric1.quantity) return;
    if (!hasAutoCalculatedRef.current.fabric1) {
      hasAutoCalculatedRef.current.fabric1 = true;
    }
    const debounceTimer = setTimeout(() => {
      handleCalculate(1);
    }, 300);
    return () => clearTimeout(debounceTimer);
    // handleCalculate bağımlılığı render başına değişebildiği için döngüye sokuyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabric1.fabricInfoId, fabric1.quantity, pastalEfficiency]);

  useEffect(() => {
    if (comparisonCount < 1) return;
    if (!fabric2.fabricInfoId || !fabric1.result?.minMeters || !fabric1.result?.maxMeters) return;
    if (!hasAutoCalculatedRef.current.fabric2) {
      hasAutoCalculatedRef.current.fabric2 = true;
    }
    const debounceTimer = setTimeout(() => {
      handleCalculate(2);
    }, 300);
    return () => clearTimeout(debounceTimer);
    // handleCalculate bağımlılığı render başına değişebildiği için döngüye sokuyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonCount, fabric2.fabricInfoId, fabric1.result, pastalEfficiency]);

  useEffect(() => {
    if (comparisonCount < 2) return;
    if (!fabric3.fabricInfoId || !fabric1.result?.minMeters || !fabric1.result?.maxMeters) return;
    const debounceTimer = setTimeout(() => {
      handleCalculate(3);
    }, 300);
    return () => clearTimeout(debounceTimer);
    // handleCalculate bağımlılığı render başına değişebildiği için döngüye sokuyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonCount, fabric3.fabricInfoId, fabric1.result, pastalEfficiency]);

  useEffect(() => {
    if (comparisonCount < 3) return;
    if (!fabric4.fabricInfoId || !fabric1.result?.minMeters || !fabric1.result?.maxMeters) return;
    const debounceTimer = setTimeout(() => {
      handleCalculate(4);
    }, 300);
    return () => clearTimeout(debounceTimer);
    // handleCalculate bağımlılığı render başına değişebildiği için döngüye sokuyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonCount, fabric4.fabricInfoId, fabric1.result, pastalEfficiency]);

  const handleSaveSettings = () => {
    if (tempPastalEfficiency < 0.1 || tempPastalEfficiency > 1.0) {
      toast.error('Pastal verimlilik 0.1 - 1.0 arası olmalıdır');
      return;
    }
    setPastalEfficiency(tempPastalEfficiency);
    setSettingsModalOpen(false);
    toast.success('Ayarlar kaydedildi');
    
    // Eğer sonuç varsa yeniden hesapla
    if (fabric1.result) {
      handleCalculate(1);
    }
    if (fabric2.result) {
      handleCalculate(2);
    }
    if (fabric3.result) {
      handleCalculate(3);
    }
    if (fabric4.result) {
      handleCalculate(4);
    }
  };

  const renderCalculatorForm = (fabricNumber) => {
    const fabricMap = {
      1: { fabric: fabric1, setFabric: setFabric1 },
      2: { fabric: fabric2, setFabric: setFabric2 },
      3: { fabric: fabric3, setFabric: setFabric3 },
      4: { fabric: fabric4, setFabric: setFabric4 }
    };
    const selected = fabricMap[fabricNumber];
    if (!selected) return null;
    const { fabric, setFabric } = selected;

    // Seçilen kumaş kartı için mevcut renkleri bul
    const selectedStockCardId = fabric.stockCardId;
    const availableColors = selectedStockCardId 
      ? fabricInfos.filter(info => info.stockCardId === parseInt(selectedStockCardId))
      : [];

    // Benzersiz stok kartlarını al ve react-select formatına çevir
    const uniqueStockCards = Array.from(
      new Map(fabricInfos.map(info => [info.stockCardId, info.stockCard])).values()
    );

    const normalizeText = (value) => {
      return (value || '')
        .toString()
        .toLowerCase()
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };

    const fabricOptions = uniqueStockCards
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr', { sensitivity: 'base' }))
      .map(stockCard => ({
      value: stockCard.id,
      label: stockCard.name
    }));

    const filterOption = (option, rawInput) => {
      const inputValue = normalizeText(rawInput);
      const optionLabel = normalizeText(option.label);
      return optionLabel.includes(inputValue);
    };

    // React-select için custom styles
    const customStyles = {
      control: (base) => ({
        ...base,
        backgroundColor: isDark ? '#374151' : '#ffffff',
        borderColor: isDark ? '#4b5563' : '#d1d5db',
        color: isDark ? '#ffffff' : '#111827',
        minHeight: '36px',
        '&:hover': {
          borderColor: isDark ? '#6b7280' : '#9ca3af'
        }
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: isDark ? '#374151' : '#ffffff',
        zIndex: 50
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused 
          ? (isDark ? '#4b5563' : '#e5e7eb') 
          : (isDark ? '#374151' : '#ffffff'),
        color: isDark ? '#ffffff' : '#111827',
        cursor: 'pointer',
        '&:active': {
          backgroundColor: isDark ? '#6b7280' : '#d1d5db'
        }
      }),
      singleValue: (base) => ({
        ...base,
        color: isDark ? '#ffffff' : '#111827'
      }),
      input: (base) => ({
        ...base,
        color: isDark ? '#ffffff' : '#111827'
      }),
      placeholder: (base) => ({
        ...base,
        color: isDark ? '#9ca3af' : '#6b7280'
      })
    };

    return (
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-2 sm:p-3`}>
        <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">
          {fabricNumber === 1 ? 'Kumaş Seçimi' : `Karşılaştırma ${fabricNumber - 1}`}
        </h3>
        
        <div className="space-y-2 sm:space-y-3">
          {/* Kumaş Seçimi - Searchable */}
          <div>
            <label className="block text-xs font-medium mb-1">Kumaş</label>
            <Select
              options={fabricOptions}
              value={fabricOptions.find(opt => opt.value === parseInt(fabric.stockCardId)) || null}
              onChange={(selectedOption) => {
                const stockCardId = selectedOption?.value || '';
                
                if (stockCardId) {
                  // Bu kumaş için mevcut renkleri bul
                  const colors = fabricInfos.filter(info => info.stockCardId === parseInt(stockCardId));
                  
                  // Renk önceliği: Siyah → Renkli → Beyaz → Dijital
                  const colorPriority = ['SIYAH', 'RENKLI', 'BEYAZ', 'DIJITAL'];
                  let defaultColor = null;
                  
                  // Eğer karşılaştırma kumaşı seçiyorsak ve fabric1'de renk seçiliyse, aynı rengi tercih et
                  if (fabricNumber !== 1 && fabric1.colorType) {
                    const sameColor = colors.find(c => c.colorType === fabric1.colorType);
                    if (sameColor) {
                      defaultColor = sameColor;
                    }
                  }
                  
                  // Aynı renk yoksa öncelik sırasına göre seç
                  if (!defaultColor) {
                    for (const priority of colorPriority) {
                      const found = colors.find(c => c.colorType === priority);
                      if (found) {
                        defaultColor = found;
                        break;
                      }
                    }
                  }
                  
                  // Eğer hiçbiri yoksa ilk rengi al
                  if (!defaultColor && colors.length > 0) {
                    defaultColor = colors[0];
                  }
                  
                  setFabric({ 
                    ...fabric, 
                    stockCardId, 
                    colorType: defaultColor?.colorType || '', 
                    fabricInfoId: defaultColor?.id || '', 
                    result: null 
                  });
                } else {
                  setFabric({ 
                    ...fabric, 
                    stockCardId: '', 
                    colorType: '', 
                    fabricInfoId: '', 
                    result: null 
                  });
                }
              }}
              styles={customStyles}
              filterOption={filterOption}
              placeholder="Kumaş ara..."
              noOptionsMessage={() => "Kumaş bulunamadı"}
              isClearable
              isSearchable
            />
          </div>

          {/* Renk Seçimi */}
          {selectedStockCardId && (
            <div>
              <label className="block text-xs font-medium mb-1">Renk</label>
              <select
                value={fabric.fabricInfoId}
                onChange={(e) => {
                  const fabricInfoId = e.target.value;
                  const selectedFabric = fabricInfos.find(f => f.id === parseInt(fabricInfoId));
                  setFabric({ 
                    ...fabric, 
                    fabricInfoId,
                    colorType: selectedFabric?.colorType || '',
                    result: null 
                  });
                }}
                className={`w-full px-3 py-1.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Renk Seçin</option>
                {availableColors.map((info) => (
                  <option key={info.id} value={info.id}>
                    {info.colorType}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Miktar - Sadece fabric1 için göster */}
          {fabricNumber === 1 && (
            <div>
              <label className="block text-xs font-medium mb-1">Miktar (gram)</label>
              <input
                type="number"
                value={fabric.quantity}
                onChange={(e) => setFabric({ ...fabric, quantity: e.target.value, result: null })}
                placeholder="Örn: 500"
                min="1"
                step="1"
                className={`w-full px-3 py-1.5 rounded-lg border text-sm ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          )}
          
          {/* Karşılaştırma için otomatik metraj bilgisi */}
          {fabricNumber !== 1 && fabric1.result?.minMeters && fabric1.result?.maxMeters && (
            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="text-[11px] text-gray-600 dark:text-gray-400">Ana Metraj (Ort.)</div>
              <div className="text-sm font-semibold">
                {((parseFloat(fabric1.result.minMeters) + parseFloat(fabric1.result.maxMeters)) / 2).toFixed(2)} m
              </div>
            </div>
          )}

          {/* Hesapla Butonu */}
          <button
            onClick={() => handleCalculate(fabricNumber)}
            disabled={loading || !fabric.fabricInfoId || (fabricNumber === 1 && !fabric.quantity) || (fabricNumber !== 1 && !fabric1.result?.minMeters)}
            className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
          >
            <Calculator className="w-4 h-4" />
            Hesapla
          </button>
        </div>
      </div>
    );
  };

  const renderResult = (result) => {
    if (!result) return null;

    return (
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-3 sm:p-4`}>
        {/* Başlık */}
        <div className="mb-2 pb-2 border-b border-gray-300 dark:border-gray-600">
          <h3 className="text-sm sm:text-base font-semibold">
            {result.fabricName} - {result.colorType}
          </h3>
        </div>

        {/* Fiyat Aralığı - Büyük */}
        <div className="text-center py-3 mb-2 border-b border-gray-300 dark:border-gray-600">
          <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
            {Math.min(result.minPriceTL, result.maxPriceTL)} - {Math.max(result.minPriceTL, result.maxPriceTL)} TL
          </div>
        </div>

        {/* Detaylar */}
        <div className="space-y-2 text-[11px] sm:text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">💵 Kumaş Fiyatı:</span>
            <span className="font-medium">
              {result.salesPrice?.toFixed(2)} USD / {result.isKgUnit ? 'KG' : 'M'}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">🧾 Girilen Gramaj:</span>
            <span className="font-medium">{Math.round(result.inputQuantity)} gr</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">⚙️ Pastal Verimlilik:</span>
            <span className="font-medium">{(result.pastalEfficiency * 100).toFixed(0)}%</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">📦 Pastal Gramajı:</span>
            <span className="font-medium">{result.requiredGrams} gr</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">📏 Gerekli Metraj:</span>
            <span className="font-medium">
              {result.minMeters && result.maxMeters ? `${result.minMeters} - ${result.maxMeters} m` : '-'}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">💱 Kur:</span>
            <span className="font-medium">{result.exchangeRate.toFixed(2)} TL</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          Kumaş maliyetini hesaplayın ve karşılaştırın
        </p>
        
        {/* Ayarlar Butonu */}
        <button
          onClick={() => {
            setTempPastalEfficiency(pastalEfficiency);
            setSettingsModalOpen(true);
          }}
          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          title="Ayarlar"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Boş Durum Mesajı */}
      {fabricInfos.length === 0 && (
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-8 text-center`}>
          <Calculator className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold mb-2">Henüz Kumaş Bilgisi Yok</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Fiyat hesaplaması yapabilmek için önce kumaş bilgilerinin eklenmesi gerekiyor.
          </p>
        </div>
      )}

      {/* Ana Grid */}
      {fabricInfos.length > 0 && (
        <>
          {comparisonCount > 0 && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => {
                  if (comparisonCount >= 3) {
                    setFabric4({ stockCardId: '', colorType: '', fabricInfoId: '', result: null });
                  } else if (comparisonCount >= 2) {
                    setFabric3({ stockCardId: '', colorType: '', fabricInfoId: '', result: null });
                  } else {
                    setFabric2({ stockCardId: '', colorType: '', fabricInfoId: '', result: null });
                  }
                  setComparisonCount(Math.max(0, comparisonCount - 1));
                }}
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Karşılaştırmayı Kapat
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {/* Sol Taraf - Kumaş 1 */}
            <div className="space-y-2 sm:space-y-3">
              {renderCalculatorForm(1)}
              {renderResult(fabric1.result)}
            </div>

            {comparisonCount > 0 ? (
              <>
                {comparisonCount >= 1 && (
                  <div className="space-y-2 sm:space-y-3">
                    {renderCalculatorForm(2)}
                    {renderResult(fabric2.result)}
                  </div>
                )}
                {comparisonCount >= 2 && (
                  <div className="space-y-2 sm:space-y-3">
                    {renderCalculatorForm(3)}
                    {renderResult(fabric3.result)}
                  </div>
                )}
                {comparisonCount >= 3 && (
                  <div className="space-y-2 sm:space-y-3">
                    {renderCalculatorForm(4)}
                    {renderResult(fabric4.result)}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center">
                <button
                  onClick={() => setComparisonCount(1)}
                  className="w-full sm:w-auto px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
                >
                  <Plus className="w-5 h-5" />
                  Karşılaştırma Ekle
                </button>
              </div>
            )}
          </div>
          {comparisonCount > 0 && comparisonCount < 3 && (
            <div className="flex justify-center mt-3">
              <button
                onClick={() => setComparisonCount(Math.min(3, comparisonCount + 1))}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Karşılaştırma Ekle
              </button>
            </div>
          )}
        </>
      )}

      {/* Ayarlar Modal */}
      {settingsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Ayarlar
              </h2>
              <button
                onClick={() => setSettingsModalOpen(false)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Pastal Verimlilik (0.1 - 1.0)
                </label>
                <input
                  type="number"
                  value={tempPastalEfficiency}
                  onChange={(e) => setTempPastalEfficiency(parseFloat(e.target.value))}
                  min="0.1"
                  max="1.0"
                  step="0.05"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Varsayılan: 0.7 (70%)
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSettingsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPriceCalculator;
